import { startOfWeek } from "date-fns";
import { Check, ClipboardPaste } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { buildProviderLink, type AiProvider } from "../../lib/schedule-document/ai-providers";
import { loadScheduleText } from "../../lib/schedule-document/load-schedule-text";
import { scheduleDocument } from "../../lib/schedule-document/schedule-json";
import { buildSchedulePrompt } from "../../lib/schedule-document/schedule-prompt";
import { useDateLocale } from "../../i18n/use-date-locale";
import { useScheduleStore } from "../../stores/schedule-store";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { AiProviderTiles, copyOnlyId } from "./AiProviderTiles";

type Notice =
  | { kind: "opened"; name: string }
  | { kind: "paste"; name: string }
  | { kind: "tooLong"; name: string }
  | { kind: "copied" }
  | { kind: "copyFailed" };

const noticeKeys = {
  opened: "ai.noticeOpened",
  paste: "ai.noticePaste",
  tooLong: "ai.noticeTooLong",
  copied: "ai.noticeCopied",
  copyFailed: "ai.noticeCopyFailed",
} as const;

function firstIssueText(error: z.ZodError) {
  const issue = error.issues[0];
  if (!issue) return "";
  const path = issue.path.join(".");
  return path ? `${path}: ${issue.message}` : issue.message;
}

// Opening a prefilled chat needs no more action. Anything that relies on the
// clipboard is a next step, so it is shown as a callout the user cannot miss.
function SendNotice({ notice, text }: { notice: Notice; text: string }) {
  const needsPaste = notice.kind === "paste" || notice.kind === "tooLong";
  if (needsPaste) {
    return (
      <p className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-900">
        <ClipboardPaste className="mt-px size-3.5 shrink-0" />{text}
      </p>
    );
  }
  const isFailure = notice.kind === "copyFailed";
  return (
    <p className={`flex items-center gap-1.5 text-[11px] font-medium ${isFailure ? "text-red-600" : "text-emerald-700"}`}>
      {!isFailure && <Check className="size-3.5 shrink-0" />}{text}
    </p>
  );
}

export function AiScheduleForm({ onApplied }: { onApplied: () => void }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const ids = { request: useId(), include: useId(), reply: useId() };
  const [request, setRequest] = useState("");
  const [includeCurrent, setIncludeCurrent] = useState(false);
  const [reply, setReply] = useState("");
  const [replyError, setReplyError] = useState("");
  const [notice, setNotice] = useState<Notice | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const slots = useScheduleStore(state => state.slots);
  const commonOptions = useScheduleStore(state => state.commonOptions);
  const offlineMessages = useScheduleStore(state => state.offlineMessages);
  const offlineActive = useScheduleStore(state => state.offlineActive);
  const selectedDate = useScheduleStore(state => state.selectedDate);
  const timezone = useScheduleStore(state => state.timezone);

  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const currentSchedule = includeCurrent
    ? scheduleDocument({ slots, commonOptions, offlineMessages, offlineActive, selectedDate, timezone })
    : undefined;
  const prompt = buildSchedulePrompt({ request, today: new Date(), weekStart, timezone, currentSchedule, dateLocale }, t);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      return true;
    } catch {
      return false;
    }
  };

  const openProvider = async (provider: AiProvider) => {
    const link = buildProviderLink(provider, prompt);
    setActiveId(provider.id);
    // Both calls run synchronously inside the click. The copy must start first:
    // the browser only allows a clipboard write while this page has focus, and
    // the new tab takes the focus as soon as it opens.
    const copying = copyPrompt();
    window.open(link.url, "_blank", "noopener");
    const copied = await copying;
    if (link.prefilled) return setNotice({ kind: "opened", name: provider.name });
    const wantedPrefill = provider.promptParam !== null;
    if (!copied) return setNotice({ kind: "copyFailed" });
    setNotice({ kind: wantedPrefill ? "tooLong" : "paste", name: provider.name });
  };

  const copyOnly = async () => {
    setActiveId(copyOnlyId);
    const copied = await copyPrompt();
    setNotice({ kind: copied ? "copied" : "copyFailed" });
  };

  const applyReply = () => {
    try {
      loadScheduleText(reply);
      setReplyError("");
      setReply("");
      onApplied();
    } catch (error) {
      const isZodError = error instanceof z.ZodError;
      if (isZodError) return setReplyError(t("ai.replyInvalid", { detail: firstIssueText(error) }));
      setReplyError(t("ai.replyNotJson"));
    }
  };

  const noticeText = notice ? t(noticeKeys[notice.kind], { name: "name" in notice ? notice.name : "" }) : "";

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label htmlFor={ids.request} className="text-[11px] font-medium text-neutral-600">{t("ai.requestLabel")}</label>
        <Textarea id={ids.request} rows={4} value={request} onChange={event => setRequest(event.target.value)}
          placeholder={t("ai.requestPlaceholder")} className="min-h-20 rounded px-2 py-1.5 text-xs shadow-none" />
        <div className="flex items-start gap-2 pt-1">
          <Checkbox id={ids.include} checked={includeCurrent} onCheckedChange={checked => setIncludeCurrent(checked === true)} className="mt-0.5" />
          <label htmlFor={ids.include} className="space-y-0.5 text-xs">
            <span className="block font-medium">{t("ai.includeCurrent")}</span>
            <span className="block text-[11px] text-neutral-500">{t("ai.includeCurrentHelp")}</span>
          </label>
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] font-medium text-neutral-600">{t("ai.sendTo")}</p>
        <AiProviderTiles activeId={activeId} onOpen={openProvider} onCopyOnly={copyOnly} />
        {notice && <SendNotice notice={notice} text={noticeText} />}
        <details className="text-xs">
          <summary className="cursor-pointer text-[11px] font-medium text-neutral-500">{t("ai.previewPrompt")}</summary>
          <Textarea readOnly rows={8} value={prompt} className="mt-1.5 rounded px-2 py-1.5 font-mono text-[11px] shadow-none" />
        </details>
      </div>

      <div className="space-y-1.5 border-t border-neutral-200 pt-3">
        <label htmlFor={ids.reply} className="text-[11px] font-medium text-neutral-600">{t("ai.replyLabel")}</label>
        <Textarea id={ids.reply} rows={5} value={reply} onChange={event => setReply(event.target.value)}
          placeholder={t("ai.replyPlaceholder")} spellCheck={false} className="rounded px-2 py-1.5 font-mono text-[11px] shadow-none" />
        {replyError && <p role="alert" className="text-xs text-red-600">{replyError}</p>}
        <Button type="button" size="sm" disabled={reply.trim().length === 0} onClick={applyReply}>{t("ai.apply")}</Button>
      </div>
    </div>
  );
}
